import { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import { useBackofficeNavigationPreference } from '@/preferences';
import type { BackofficeNavigationTopLevelItemId } from './backoffice-navigation-items';

type BackofficeNavigationLinksProps = {
  onNavigate?: () => void;
};

export function BackofficeNavigationLinks({ onNavigate }: BackofficeNavigationLinksProps) {
  const { visibleItems } = useBackofficeNavigationPreference();
  const { pathname } = useLocation();
  const [expandedItemId, setExpandedItemId] = useState<BackofficeNavigationTopLevelItemId | null>(
    null
  );

  useEffect(() => {
    const activeParent = visibleItems.find(
      (item) =>
        item.children?.length && (pathname === item.to || pathname.startsWith(`${item.to}/`))
    );
    setExpandedItemId(activeParent?.id ?? null);
  }, [pathname, visibleItems]);

  return (
    <ul className="space-y-3">
      {visibleItems.map((item) => {
        const isExpanded = expandedItemId === item.id;
        const hasChildren = Boolean(item.children?.length);
        const childMenuId = `backoffice-navigation-${item.id}-children`;

        return (
          <li key={item.id} className="group">
            <div className="flex items-center">
              <NavLink
                to={item.to}
                end={item.end ?? false}
                onClick={() => {
                  setExpandedItemId(hasChildren ? item.id : null);
                  onNavigate?.();
                }}
                className={({ isActive }) =>
                  [
                    'focus-ring flex min-w-0 flex-1 items-center gap-3 rounded-md px-1 py-2 text-body font-medium transition',
                    isActive ? 'text-neutral-900' : 'text-neutral-600 hover:text-backoffice-600',
                  ].join(' ')
                }
              >
                <item.Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="min-w-0 truncate">{item.label}</span>
              </NavLink>
              {hasChildren ? (
                <button
                  type="button"
                  aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${item.label} submenu`}
                  aria-controls={childMenuId}
                  aria-expanded={isExpanded}
                  onClick={() => {
                    setExpandedItemId((current) => (current === item.id ? null : item.id));
                  }}
                  className="focus-ring -mr-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-neutral-500 transition hover:bg-white hover:text-neutral-900 lg:opacity-0 lg:group-focus-within:opacity-100 lg:group-hover:opacity-100"
                >
                  {isExpanded ? (
                    <ChevronUp className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <ChevronDown className="h-4 w-4" aria-hidden="true" />
                  )}
                </button>
              ) : null}
            </div>
            {hasChildren && isExpanded ? (
              <ul id={childMenuId} className="ml-7 mt-1 space-y-1">
                {item.children?.map((child) => (
                  <li key={child.id}>
                    <NavLink
                      to={child.to}
                      end={child.end ?? false}
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        [
                          'focus-ring flex items-center gap-3 rounded-md px-1 py-1.5 text-small font-medium transition',
                          isActive
                            ? 'text-neutral-900'
                            : 'text-neutral-600 hover:text-backoffice-600',
                        ].join(' ')
                      }
                    >
                      <child.Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                      {child.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
